import { debounce, isString } from "es-toolkit";
import { useEffect, useRef, useTransition } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search as SearchBase,
  onEnterKey,
} from "@songtradr-one/components-frontend";
import { Props } from "./Search.Parts";

export const Search = ({
  type,
  placeholder,
  focusOnQuery,
  debounceTime = 1800,
  defaultActive = false,
  autoFocus: autoFocusProp,
  search: searchProp,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, startTransition] = useTransition();

  const defaultSearch = (query: string) => {
    startTransition(() => {
      setSearchParams(
        (prevSearchParams) => {
          if (query) prevSearchParams.set("query", query);
          else prevSearchParams.delete("query");
          prevSearchParams.set("page", "1");
          return prevSearchParams;
        },
        { replace: true },
      );
    });
  };

  const search = searchProp ?? defaultSearch;

  // The debounced call outlives the render it was created in, so it reads the
  // latest `search` - an old one would write back URL params changed since.
  const searchRef = useRef(search);
  useEffect(() => {
    searchRef.current = search;
  });

  const debouncedSearch = useRef(
    debounce((query: string) => searchRef.current(query), debounceTime),
  ).current;

  const handleKeyDown = onEnterKey<HTMLInputElement>((event) => {
    debouncedSearch.cancel();
    search(event.currentTarget.value);
  });

  const autoFocus =
    autoFocusProp || (focusOnQuery && isString(searchParams.get("query")));

  // Outer fn returns the cleanup fn, so cancel() only runs on unmount
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <SearchBase
      type={type}
      autoFocus={autoFocus}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      defaultActive={
        defaultActive || autoFocus || Boolean(searchParams.get("query"))
      }
      defaultValue={searchParams.get("query") || ""}
      onChange={(event) => {
        const query = event.target.value;
        debouncedSearch(query);
      }}
    />
  );
};
